/**
 *  公共组件所需的接口api文件
 *  @module lots/api
 */
import localforage from 'localforage';
import _ from 'lodash';
import request from '@/utils/http';
import { getSession, setSession, getLocal, setLocal } from '@/components/utils/storage';
import store from '@/store';
import DictQueue from '../utils/DictQueue';
import config from '@/config/user.env';
const LINKSYM = '#';
/**
 * 省市区相关接口
 */

/** 初始化indexedDb数据表 */
const addressDb = localforage.createInstance({
    storeName: 'address'
});

// 数据源是网货的区域数据
const addressDbByNTP = localforage.createInstance({
    storeName: 'addressByNTP2' // key修改下，上线后才不用让用户清缓存
});

// 行政区域数据，汉字拼音首字母排序
const areaFirstLetterSort = (areaList) => {
    areaList.sort((a, b) => {
        // return a.ebplNameCn.localeCompare(b.ebplNameCn, 'zh');
        return (a.ebplNameFirstEn || '').localeCompare(b.ebplNameFirstEn);
    })
    return areaList
}

/**
 * 获取省市区区域数据
 * @function getAreaApi
 * @static
 * @param {Object} params 获取区域数据的参数，详看 /api-mdm/searchEbPlace 接口文档
 * @returns {Object} 响应数据
 */
export const getAreaApi = async params => {
    const { identityInfo } = store.getters;
    const areaList = await addressDb.getItem(String(params.ebplParentPmCode));
    if (areaList) {
        return {
            code: '0',
            data: areaList
        };
    } else {
        params.ebplIsAble = 'ENABLE';
        let url = '/api-mdm/searchEbPlace';
        // hack 网络货运平台-当前是货主时使用public接口
        if (identityInfo && identityInfo.isHz) {
            url = '/api-mdm/public/searchEbPlace';
        }
        return request({
            url,
            method: 'get',
            params: params
        }).then(res => {
            if (res && res.code === '0' && !_.isEmpty(res.data)) {
                addressDb.setItem(String(params.ebplParentPmCode), res.data);
            }
            return res;
        });
    }
};

/**
 * 获取行政区域数据，数据来源于网货
 * @function getAreaByNTP
 * @param {Object} params 获取区域数据的参数，{
        "parentCode": "string", // 上级行政区域编码
        "type": "string" // PLACE_PROVINCE-省级；PLACE_CITY-市级；PLACE_DISTRICT-区级；PLACE_STREET-镇级；5-街
    }
 * @returns {Object} 响应数据
 */
export const getAreaByNTP = async params => {
    const rqParams = {
        parentCode: params.ebplParentPmCode,
        type: params.ebplType,
    }
    const areaList = await addressDbByNTP.getItem(String(params.ebplParentPmCode));
    if (areaList) {
        return {
            code: '0',
            data: areaFirstLetterSort(areaList), // 汉字拼音首字母排序
        };
    } else {
        return request({
            url: '/api-ntp/ntp/administrative/queryAdministrativeRegion',
            method: 'post',
            data: rqParams
        }).then(res => {
            if (res && res.code === '0' && !_.isEmpty(res.data)) {
                areaFirstLetterSort(res.data) // 汉字拼音首字母排序
                addressDbByNTP.setItem(String(params.ebplParentPmCode), res.data);
            }
            return res;
        });
    }
};

/**
 * 通过搜索关键字查询行政区域数据。
 * @function getAreaByKeyWords
 * @static
 * @param {Object} params 获取区域数据的参数，{
        "keywords": "string", // 关键词
        "types": [ // PLACE_PROVINCE-省级；PLACE_CITY-市级；PLACE_DISTRICT-区级；PLACE_STREET-镇级；5-街
            "string" // 比如要搜索省级、市级的数据，就传types: [PLACE_PROVINCE, PLACE_CITY]
        ]
    }
 * @returns {Object} 响应数据
 */
export const getAreaByKeyWords = async params => {
    return request({
        url: '/api-ntp/ntp/administrative/queryAdministrativeRegionByKeyWords',
        method: 'post',
        data: params
    }).then(res => {
        return res;
    });
};

/**
 * 列配置相关接口
 */

/** 列配置和自定义搜索条件配置相关接口 */
export const getList = {
    post(url, params) {
        const key = [params.id, params.subjectType, params.moduleCode].join(LINKSYM);
        Reflect.deleteProperty(params, 'id');
        setLocal(key, params.configName);
        return request({
            url: url + '/userIndividualConfigs/saveOrUpdate',
            method: 'post',
            data: params
        });
    },
    get(url, params) {
        const key = [params.id, params.subjectType, params.moduleCode].join(LINKSYM);
        Reflect.deleteProperty(params, 'id');
        if (getLocal(key)) {
            return Promise.resolve({
                code: '0',
                data: {
                    configName: getLocal(key)
                }
            });
        } else {
            return request({
                url: url + '/userIndividualConfigs/param',
                method: 'post',
                data: params
            }).then(res => {
                if (res.code === '0' && res.data.configName) {
                    setLocal(key, res.data.configName);
                }
                return res;
            });
        }
    }
};

/** 列配置和自定义搜索条件配置相关接口2 */
export const fieldConfig = {

    set: params => {
        let url = '';
        if (store.state.user.identityInfo && store.state.user.identityInfo.isHz) {
            url = '/api-ntp/ntp/apiAuth'
        } else {
            url = '/api-auth'
        }

        return getList.post(url, params);
    },
    get: params => {
        let url = '';
        if (store.state.user.identityInfo && store.state.user.identityInfo.isHz) {
            url = '/api-ntp/ntp/apiAuth'
        } else {
            url = '/api-auth'
        }

        return getList.get(url, params);
    }
};

/**
 *  数据字典相关接口
 */

/** 获取缓存字典
 *  @param {string} keyStr 数据字典key串，可批量用,分隔
 *  @returns {Object} 返回key值跟对应的字典内容
 */
export function getDictCache(keysStr) {
    if (!keysStr) {
        return {
            result: {},
            passKeys: ''
        };
    }

    const tenantCode = store.getters.currentTenant.tenantCode;
    const locale = store.getters.locale || 'zh-CN';
    const keys = keysStr.split(',');
    const result = {};
    const passKeys = [];
    keys.forEach(key => {
        const sessionKey = [tenantCode, locale, key].join(LINKSYM);
        const val = getSession(sessionKey);
        if (val) {
            result[key] = val;
        } else {
            passKeys.push(key);
        }
    });
    return {
        result,
        passKeys: passKeys.join(',')
    };
}
/** 设置字典缓存
 *  @param {Object} map key-value映射对象
 */
export function setDictCache(map) {
    const tenantCode = store.getters.currentTenant.tenantCode;
    const locale = store.getters.locale || 'zh-CN';
    Object.keys(map).forEach(key => {
        if (_.isEmpty(map[key])) return;
        const sessionKey = [tenantCode, locale, key].join(LINKSYM);
        setSession(sessionKey, map[key]);
    });
}
/**
 * 字典获取函数
 * @function dict
 * @static
 * @param {String} popPrefix 字典接口前缀
 * @param {Object|String} params 字典参数
 * @returns {Promise<Object>} 响应结果
 */
export const dict = (popPrefix, params) => {
    let dictCodes = null;
    if (_.isString(params)) {
        dictCodes = params;
    } else {
        dictCodes = params.dictCodes;
    }
    const data = getDictCache(dictCodes);
    if (data.passKeys === '') {
        return new Promise(resolve => {
            resolve({
                code: '0',
                data: data.result
            });
        });
    } else {
        return request({
            url: popPrefix,
            method: 'get',
            params: {
                // 不区分租户
                tenantCode: store.getters.currentTenant.tenantCode,
                applicationCode: params.applicationCode || store.getters.currentTenant.applicationCode || store.getters.currentApplicationCode,
                dictCodes: data.passKeys
            }
        }).then(res => {
            setDictCache(res.data);
            res.data = { ...data.result, ...res.data };
            return res;
        });
    }
};

/** 初始化队列对象 */
const dictQueue = new DictQueue();

/**
 * 获取数据字典（带队列版）
 * @function dictWithUrl
 * @static
 * @param {Object|string} params 数据字典参数
 * @param {string} url 数据字典接口地址
 * @returns {Promise<Object>} 返回队列promise
 */
// export const dictWithUrl = (params, url = '/api-ds/dictionaryDetail/dict') => {
//     dictQueue.setFetchFn(params => dict(url, params));
//     const dictCodes = _.isString(params) ? params : params.dictCodes;
//     return dictQueue.queueUp(dictCodes);
// };
const DefaultDcDictPath = config.dcDictPath || '/api-mdm/public/dictionaryDetail/dict'
export const dictWithUrl = (params, url = DefaultDcDictPath) => {
    dictQueue.setFetchFn(params => dict(url, params));
    const dictCodes = _.isString(params) ? params : params.dictCodes;
    return dictQueue.queueUp(dictCodes);
};

/**
 * @param {Object} obj  字典返回数据
 * @returns {Array} result 字典key-value数组
 */
export const formatObjToDatalist = obj => {
    if (!obj) return [];
    const result = [];
    Object.keys(obj).forEach(k => {
        result.push({ label: obj[k], value: k });
    });
    return result;
};
